import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { captureException } from '@sentry/angular';
import { combineLatest, EMPTY, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Location, SchoolActivity, SchoolActivityInstance, User } from '../../models';
import { AttendanceRecordState } from '../../models/AttendanceRecord';
import { AttendanceRecordService } from '../../services/attendance-record-service';
import { KioskModeService } from '../../services/kiosk-mode.service';
import { SchoolActivityService } from '../../services/school-activity.service';
import { ToastService } from '../../services/toast.service';

export type KioskCheckInDialogData = {
	currentLocation: Location;
};

@Component({
	selector: 'sp-kiosk-check-in-dialog',
	templateUrl: './kiosk-check-in-dialog.component.html',
	styleUrls: ['./kiosk-check-in-dialog.component.scss'],
})
export class KioskCheckInDialogComponent implements OnInit, OnDestroy {
	constructor(
		public dialogRef: MatDialogRef<KioskCheckInDialogComponent>,
		@Inject(MAT_DIALOG_DATA) private data: KioskCheckInDialogData,
		private kioskMode: KioskModeService,
		private activityService: SchoolActivityService,
		private attendanceRecordService: AttendanceRecordService,
		private toast: ToastService
	) {}

	private attendanceEnum = AttendanceRecordState;
	bufferMinutes = 5;
	loading = false;
	findById = false;
	findByName = true;
	findByScan = false;

	availableActivities: { instance: SchoolActivityInstance; activity: SchoolActivity }[] = [];
	selectedActivity: { instance: SchoolActivityInstance; activity: SchoolActivity } | undefined;

	private destroy$: Subject<void> = new Subject<void>();

	ngOnInit(): void {
		const settings = this.kioskMode.getKioskModeSettings();
		this.findById = settings.findById;
		this.findByName = settings.findByName;
		this.findByScan = settings.findByScan;

		this.kioskMode.checkInShowing$.next(true);

		// You can use this test data for your application or further manipulate it as needed.
		/*
		this.availableActivities = [
			{
				instance: {
					id: 1,
					start_time: '2023-08-01T09:00:00',
					end_time: '2023-08-01T10:30:00',
					activity_id: 101,
					user_id: 201,
					school_id: 301,
					created_at: '2023-08-01T08:30:00',
					updated_at: '2023-08-01T08:30:00',
					current_num_attendees: 10,
					state: 'scheduled',
					selected: false,
				},
				activity: {
					id: 101,
					icon: '🎨',
					name: 'Art Club',
					description: 'Join us for a fun and creative art session!',
					user_id: 201,
					location_id: 401,
					flex_period_id: 501,
					max_attendees: 20,
					public_event: true,
					teacher_name: 'Ms. Smith',
					profile_picture: 'https://example.com/profiles/ms-smith.jpg',
					state: 'scheduled',
				},
			},
			{
				instance: {
					id: 2,
					start_time: '2023-08-01T11:00:00',
					end_time: '2023-08-01T12:30:00',
					activity_id: 102,
					user_id: 202,
					school_id: 301,
					created_at: '2023-08-01T10:30:00',
					updated_at: '2023-08-01T10:30:00',
					current_num_attendees: 5,
					state: 'flex_recurring',
					selected: true,
				},
				activity: {
					id: 102,
					icon: '📚',
					name: 'Book Club',
					description: 'Explore the fascinating world of books and literature!',
					user_id: 202,
					location_id: 402,
					max_attendees: 15,
					public_event: true,
					state: 'flex_recurring',
				},
			},
		];
		*/

		this.loading = true;

		this.activityService
			.GetStartedActivityInstances(this.data.currentLocation.id, this.bufferMinutes)
			.pipe(
				switchMap((instances) => {
					const activity_ids = instances.map((i) => i.activity_id);
					if (activity_ids.length === 0) {
						this.loading = false;
						return of([[], []]);
					}

					return combineLatest([this.activityService.GetActivitiesHTTP({ ids: activity_ids }), of(instances)]);
				}),
				catchError((error) => {
					captureException(error, {
						extra: {
							type: 'Error retrieving activities and instances',
							component: 'sp-kiosk-check-in-dialog',
						},
					});
					return EMPTY;
				}),
				takeUntil(this.destroy$)
			)
			.subscribe(([activities, instances]) => {
				this.availableActivities = instances
					.map((i) => {
						const activity = activities.find((a) => a.id === i.activity_id);
						return { instance: i, activity: activity! };
					})
					.filter((o) => !!o.activity);

				if (this.availableActivities.length === 1) {
					this.selectedActivity = this.availableActivities[0];
				}
				this.loading = false;
			});
	}

	selectActivity(event: Event, activity: { instance: SchoolActivityInstance; activity: SchoolActivity }): void {
		// Make the border green
		const clickedElement = event.target as HTMLElement;
		clickedElement.classList.toggle('active');
		setTimeout(() => {
			clickedElement.classList.toggle('active');
			this.selectedActivity = activity;
		}, 300);
	}

	checkInStudent(student: User, activityInstance: SchoolActivityInstance, activity: SchoolActivity): void {
		const existingRecord$ = this.attendanceRecordService
			.getRecordsForInstanceId(activityInstance.id)
			.pipe(map((records) => records.find((r) => r.attendance_user.id === student.id)));

		const state =
			new Date().getTime() < new Date(activityInstance.start_time).getTime() ? this.attendanceEnum.Present : this.attendanceEnum.LateNotExcused;

		existingRecord$
			.pipe(
				take(1),
				takeUntil(this.destroy$),
				switchMap((existing) => {
					if (existing?.state === this.attendanceEnum.NotSelected) {
						return this.attendanceRecordService
							.updateRecordState({
								id: existing.id,
								state: state,
							})
							.pipe(
								tap({
									next: () => {
										this.toast.openToast({ title: `${student.display_name} successfully checked into ${activity.name}`, type: 'success' });
										this.dialogRef.close();
									},
									error: () => {
										this.toast.openToast({ title: 'Failed to check in', subtitle: 'Please try again later.', type: 'error' });
									},
								}),
								catchError(() => EMPTY)
							);
					} else {
						if (existing) {
							this.toast.openToast({ title: 'You are already checked in', subtitle: 'Please ask your teacher for help.', type: 'error' });
						} else {
							this.toast.openToast({
								title: 'Student not signed up',
								subtitle: `You need to be signed up for ${activity.name} to check into it.`,
								type: 'error',
							});
						}
						return EMPTY;
					}
				})
			)
			.subscribe();
	}

	ngOnDestroy(): void {
		this.kioskMode.checkInShowing$.next(false);
		this.destroy$.next();
		this.destroy$.complete();
	}

	back(): void {
		if (this.selectedActivity) {
			this.selectedActivity = undefined;
			return;
		}
		this.dialogRef.close();
	}
}
